import { api } from '@/services/api/AxiosBaseService'
import type { SingleResponse, PaginatedResponse, PaginationMeta } from '@/types/api'
import type { Revenue, EarningsReport, CommissionsReport, CommissionTransaction, CommissionDetail } from './RevenuesService'

/**
 * Dashboard do parceiro — centrado nas COMISSÕES dele + adesões da rede.
 * Nunca expõe faturamento sensível das empresas/associados.
 */
export interface PartnerDashboard {
  yearlyCommissions: { year: number; total: number; growthPct: number | null }[]
  currentMonth: {
    commissionsReceived: { count: number; amount: number }
    commissionsPending: { count: number; amount: number }
    commissionsToPay: { count: number; amount: number }
    adhesions: { count: number }
  }
  monthlyAdhesions: { month: string; label: string; count: number }[]
  activeSubscriptions: { total: number; byType: { type: string; total: number }[] }
  latestSubscribers: {
    id: string; name: string; code: string; planName: string | null; status: string
    createdAt: string | null; formatted: { createdAt?: string }
  }[]
}

export interface NetworkUser {
  id: string
  name: string
  username: string
  email: string | null
  whatsapp: string | null
  type: string
  status: string
  slug: string | null
  managerName: string | null
  commissionName: string | null
}

/** Detalhe completo (carregado no form de edição da rede). */
export interface NetworkUserDetail extends NetworkUser {
  commissionId: string | null
  administrativeManagerName: string | null
  administrativeManagerEmail: string | null
  billingManagerName: string | null
  billingManagerEmail: string | null
  address: {
    postalCode: string | null; address: string | null; number: string | null
    complement: string | null; district: string | null; cityId: string | null; stateId: string | null
  } | null
  bank: {
    bankId: string | null; beneficiary: string | null; document: string | null
    agency: string | null; account: string | null; pixKey: string | null
    transaction: string | null; type: string | null; accountType: string | null
  } | null
}

export interface PartnerSalesLink {
  slug: string | null
  name: string
  plans: { id: string; name: string; price: number }[]
}

export const PartnerService = {
  // Dashboard — comissões do parceiro + adesões da rede dele
  async dashboard(): Promise<PartnerDashboard> {
    const r = await api.get<SingleResponse<PartnerDashboard>>('/partner/dashboard')
    return r.data.data
  },

  // Faturas (read-only)
  async invoices(params: Record<string, unknown> = {}): Promise<PaginatedResponse<Revenue>> {
    const r = await api.get<PaginatedResponse<Revenue>>('/partner/invoices', { params })
    return r.data
  },
  async invoice(id: string): Promise<Revenue> {
    const r = await api.get<SingleResponse<Revenue>>(`/partner/invoices/${id}`)
    return r.data.data
  },

  // Relatórios
  async reportsEarnings(from: string, to: string): Promise<EarningsReport> {
    const r = await api.get<SingleResponse<EarningsReport>>('/partner/reports/earnings', { params: { date_start: from, date_end: to } })
    return r.data.data
  },
  async reportsCommissions(from: string, to: string): Promise<CommissionsReport> {
    const r = await api.get<SingleResponse<CommissionsReport>>('/partner/reports/commissions', { params: { date_start: from, date_end: to } })
    return r.data.data
  },
  async reportsCommissionTransactions(p: { from: string; to: string; type?: string; page?: number; size?: number }) {
    const r = await api.get<{ data: CommissionTransaction[]; meta: PaginationMeta }>('/partner/reports/commission-transactions', {
      params: { date_start: p.from, date_end: p.to, ...(p.type ? { type: p.type } : {}), page: p.page ?? 1, size: p.size ?? 25 },
    })
    return r.data
  },
  // Detalhamento (drawer) de um card de "Suas comissões" em /partner/reports/earnings.
  async reportsEarningsDetail(p: { from: string; to: string; role: 'receive' | 'pay'; status: 'consolidado' | 'pendente' }): Promise<CommissionDetail> {
    const r = await api.get<SingleResponse<CommissionDetail>>('/partner/reports/earnings-detail', {
      params: { date_start: p.from, date_end: p.to, role: p.role, status: p.status },
    })
    return r.data.data
  },
  // Detalhamento (drawer) de uma célula da tabela em /partner/reports/commissions.
  async reportsCommissionDetail(p: { from: string; to: string; memberId?: string; type?: string; status: 'consolidado' | 'pendente' }): Promise<CommissionDetail> {
    const r = await api.get<SingleResponse<CommissionDetail>>('/partner/reports/commission-detail', {
      params: {
        date_start: p.from, date_end: p.to, status: p.status,
        ...(p.memberId ? { member_id: p.memberId } : {}),
        ...(p.type ? { type: p.type } : {}),
      },
    })
    return r.data.data
  },

  // Minha rede
  async network(params: Record<string, unknown> = {}) {
    const r = await api.get<{ data: NetworkUser[]; meta: PaginationMeta }>('/partner/network', { params })
    return r.data
  },
  async networkShow(id: string): Promise<NetworkUserDetail> {
    const r = await api.get<SingleResponse<NetworkUserDetail>>(`/partner/network/${id}`)
    return r.data.data
  },
  async allowedTypes(): Promise<string[]> {
    const r = await api.get<SingleResponse<string[]>>('/partner/network/allowed-types')
    return r.data.data
  },
  async networkCreate(payload: Record<string, unknown>): Promise<NetworkUser> {
    const r = await api.post<SingleResponse<NetworkUser>>('/partner/network', payload)
    return r.data.data
  },
  async networkUpdate(id: string, payload: Record<string, unknown>): Promise<NetworkUser> {
    const r = await api.put<SingleResponse<NetworkUser>>(`/partner/network/${id}`, payload)
    return r.data.data
  },
  async networkStatus(id: string, status: string): Promise<void> {
    await api.put(`/partner/network/${id}/status`, { status })
  },

  // Links > Vendas
  async salesLink(): Promise<PartnerSalesLink> {
    const r = await api.get<SingleResponse<PartnerSalesLink>>('/partner/sales-link')
    return r.data.data
  },
}
